import type { TabSize } from "@/components/flexkit/tabs/NavigationTabs";
import { cn } from "@/components/podkit/lib/cn";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";
import { useCallback, useEffect, useRef, type ReactNode } from "react";

const INDICATOR_TRANSITION = "transform 200ms ease";

/**
 * A horizontally-scrollable tab list with a sliding indicator.
 *
 * Use this instead of NavigationTabs.List when the tab bar can accumulate
 * enough tabs to overflow (e.g. the workbench left panel). Tabs maintain
 * their natural width (up to max-w-60) and scroll instead of shrinking.
 *
 * The indicator is positioned via direct DOM manipulation to avoid React
 * re-renders on every scroll frame. The bottom border lives on a
 * non-scrolling wrapper so it stays fixed during horizontal scroll.
 */
export const ScrollableTabList = React.forwardRef<
    HTMLDivElement,
    {
        children: ReactNode;
        className?: string;
        /** Override the bottom border color on the non-scrolling wrapper. */
        borderClassName?: string;
        size?: TabSize;
    }
>(({ children, className, borderClassName, size = "md" }, ref) => {
    const listRef = useRef<HTMLDivElement>(null);
    const indicatorRef = useRef<HTMLDivElement>(null);
    const fadeLeftRef = useRef<HTMLDivElement>(null);
    const fadeRightRef = useRef<HTMLDivElement>(null);

    React.useImperativeHandle(ref, () => listRef.current!);

    // True while a programmatic scrollIntoView is animating — suppresses
    // scroll-driven indicator updates to prevent jitter.
    const isAutoScrolling = useRef(false);
    const autoScrollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Cached reference to the active tab element. Updated only by the
    // MutationObserver so scroll/resize handlers avoid DOM queries.
    const activeElementRef = useRef<HTMLElement | null>(null);

    // Update the left/right fade visibility based on scroll position.
    // Uses direct DOM manipulation to avoid re-renders on every scroll frame.
    const applyFades = useCallback(() => {
        const list = listRef.current;
        const left = fadeLeftRef.current;
        const right = fadeRightRef.current;
        if (!list || !left || !right) return;

        const { scrollLeft, scrollWidth, clientWidth } = list;
        const threshold = 2; // px tolerance for sub-pixel rounding

        left.style.opacity = scrollLeft > threshold ? "1" : "0";
        right.style.opacity = scrollWidth - scrollLeft - clientWidth > threshold ? "1" : "0";
    }, []);

    // Snap the indicator to the active tab's visible position.
    // Uses transform (translateX + scaleX) instead of left/width so the
    // animation is GPU-composited and won't jank when heavy tab content
    // is mounting on the main thread.
    const applyIndicator = useCallback(() => {
        const list = listRef.current;
        const el = indicatorRef.current;
        const active = activeElementRef.current;
        if (!list || !el) return;

        if (!active || !list.contains(active)) {
            el.style.display = "none";
            return;
        }

        const x = active.offsetLeft - list.scrollLeft;
        const w = active.offsetWidth;
        el.style.display = "";
        el.style.transform = `translateX(${x}px) scaleX(${w})`;
    }, []);

    const applyAll = useCallback(() => {
        applyIndicator();
        applyFades();
    }, [applyIndicator, applyFades]);

    /** Scroll a tab into view, tracking the indicator on each frame. */
    const scrollTabIntoView = useCallback(
        (tab: HTMLElement) => {
            const list = listRef.current;
            // Skip if the tab is already fully visible — no scroll needed,
            // so the indicator transition should not be suppressed.
            if (list) {
                const listRect = list.getBoundingClientRect();
                const tabRect = tab.getBoundingClientRect();
                if (tabRect.left >= listRect.left && tabRect.right <= listRect.right) {
                    return;
                }
            }

            isAutoScrolling.current = true;
            const el = indicatorRef.current;
            if (el) el.style.transition = "none";
            tab.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "nearest" });

            // Track the scroll with a rAF loop so the indicator stays
            // locked to the tab as it scrolls into view.
            let rafId: number;
            const track = () => {
                applyAll();
                if (isAutoScrolling.current) {
                    rafId = requestAnimationFrame(track);
                }
            };
            rafId = requestAnimationFrame(track);

            // Use scrollend to stop tracking precisely, with a fallback timer.
            const settle = () => {
                isAutoScrolling.current = false;
                cancelAnimationFrame(rafId);
                if (el) el.style.transition = INDICATOR_TRANSITION;
                applyAll();
            };

            if (autoScrollTimer.current) clearTimeout(autoScrollTimer.current);
            if (list && "onscrollend" in list) {
                const onScrollEnd = () => {
                    settle();
                    list.removeEventListener("scrollend", onScrollEnd);
                    if (autoScrollTimer.current) clearTimeout(autoScrollTimer.current);
                };
                list.addEventListener("scrollend", onScrollEnd, { once: true });
                autoScrollTimer.current = setTimeout(onScrollEnd, 500);
            } else {
                autoScrollTimer.current = setTimeout(settle, 500);
            }
        },
        [applyAll],
    );

    useEffect(() => {
        const list = listRef.current;
        if (!list) return;

        // Initialize the cached active element and position the indicator.
        activeElementRef.current = list.querySelector<HTMLElement>('[data-state="active"]');
        applyAll();

        // Radix toggles data-state on the triggers when the active tab changes.
        const observer = new MutationObserver(() => {
            const active = list.querySelector<HTMLElement>('[data-state="active"]');
            if (active && active !== activeElementRef.current) {
                activeElementRef.current = active;
                applyAll();
                scrollTabIntoView(active);
            } else {
                activeElementRef.current = active;
                applyAll();
            }
        });
        observer.observe(list, { attributes: true, attributeFilter: ["data-state"], subtree: true });

        // Recompute on resize.
        const resizeObserver = new ResizeObserver(applyAll);
        resizeObserver.observe(list);
        for (const trigger of list.querySelectorAll<HTMLElement>('[role="tab"]')) {
            resizeObserver.observe(trigger);
        }

        // Track user-initiated scrolling — suppress the transition so the
        // indicator tracks the scroll position instantly.
        let userScrollTimer: ReturnType<typeof setTimeout> | null = null;
        const onScroll = () => {
            if (isAutoScrolling.current) return;
            const el = indicatorRef.current;
            if (el) el.style.transition = "none";
            if (userScrollTimer) clearTimeout(userScrollTimer);
            userScrollTimer = setTimeout(() => {
                if (el) el.style.transition = INDICATOR_TRANSITION;
            }, 150);
            applyAll();
        };
        list.addEventListener("scroll", onScroll, { passive: true });

        // Scroll focused tabs into view for keyboard navigation.
        const onFocusIn = (e: FocusEvent) => {
            const target = e.target;
            if (target instanceof HTMLElement && target.role === "tab") {
                scrollTabIntoView(target);
            }
        };
        list.addEventListener("focusin", onFocusIn);

        return () => {
            observer.disconnect();
            resizeObserver.disconnect();
            list.removeEventListener("scroll", onScroll);
            list.removeEventListener("focusin", onFocusIn);
            if (autoScrollTimer.current) clearTimeout(autoScrollTimer.current);
            if (userScrollTimer) clearTimeout(userScrollTimer);
        };
    }, [applyAll, scrollTabIntoView, children]);

    return (
        <div className={cn("relative min-w-0 overflow-x-clip border-b border-border-base", borderClassName)}>
            <TabsPrimitive.List
                ref={listRef}
                className={cn(
                    "scrollbar-hide flex items-center gap-1 overflow-x-auto",
                    size === "sm" ? "h-8" : "h-10",
                    className,
                )}
            >
                {children}
            </TabsPrimitive.List>
            {/* Indicator lives in the wrapper (outside the scrollable list)
                so it isn't clipped by overflow and can overlap the border.
                Positioned via direct DOM manipulation to avoid re-renders. */}
            <div
                ref={indicatorRef}
                aria-hidden
                className="nav-tabs-indicator pointer-events-none absolute -bottom-px left-0 z-10 h-0.5 w-px origin-left bg-content-primary"
                style={{ transition: INDICATOR_TRANSITION }}
            />
            {/* Scroll-edge fade gradients — opacity toggled via DOM to avoid re-renders. */}
            <div
                ref={fadeLeftRef}
                aria-hidden
                className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-6 bg-gradient-to-r from-surface-glass to-transparent transition-opacity duration-150"
                style={{ opacity: 0 }}
            />
            <div
                ref={fadeRightRef}
                aria-hidden
                className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-6 bg-gradient-to-l from-surface-glass to-transparent transition-opacity duration-150"
                style={{ opacity: 0 }}
            />
        </div>
    );
});
ScrollableTabList.displayName = "ScrollableTabList";
